/**
 * D417: "Delete account" in Settings, for a guest and a signed-in player alike. The sheet says
 * what goes and that it cannot be undone, and Delete is live only once `delete` is typed. On
 * success the device forgets what it kept for the account and reloads into a first visit.
 */
import { useState } from 'preact/hooks';
import {
  DELETE_ACCOUNT,
  DELETE_ACCOUNT_FAILED,
  DELETE_ACCOUNT_GOES,
  DELETE_ACCOUNT_INPUT,
  DELETE_ACCOUNT_NEEDS_WORD,
  DELETE_ACCOUNT_TYPE,
  DELETE_ACCOUNT_WORD,
  DELETE_WORLD_CANCEL,
  DELETE_WORLD_UNDO,
  deleteAccountLoses,
  REASON_WAIT,
} from '../game/copy.ts';
import { deleteAccount } from '../game/sync.ts';
import { Button } from '../ui/Button.tsx';
import { GhostRingButton } from './SettingsRows.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { SECURE_SNOOZE_KEY } from './account-prompt.ts';
import { DEFAULT_WORLD_KEY, LANDING_MIN_KEY } from './world-pref.ts';

/** Every idle key on this device is about the account's worlds: tips, orders, the stub world. */
const IDLE_PREFIX = 'hedgerow-idle-';

/** What the device kept for the deleted account. Sound and display choices stay. */
function forgetAccountState(): void {
  try {
    const store = globalThis.localStorage;
    if (!store) return;
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key?.startsWith(IDLE_PREFIX)) keys.push(key);
    }
    keys.push(DEFAULT_WORLD_KEY, LANDING_MIN_KEY, SECURE_SNOOZE_KEY);
    for (const key of keys) store.removeItem(key);
  } catch {
    // Private windows have no storage, so nothing was kept.
  }
}

export function DeleteAccountButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <GhostRingButton testId="delete-account-button" onClick={() => setOpen(true)}>
        {DELETE_ACCOUNT}
      </GhostRingButton>
      {open && <DeleteAccountSheet onClose={() => setOpen(false)} />}
    </>
  );
}

function DeleteAccountSheet({ onClose }: { onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = typed.trim().toLowerCase() === DELETE_ACCOUNT_WORD;
  const confirm = async () => {
    setBusy(true);
    setError(null);
    if (!(await deleteAccount())) {
      setBusy(false);
      setError(DELETE_ACCOUNT_FAILED);
      return;
    }
    forgetAccountState();
    globalThis.location.replace(globalThis.location.pathname);
  };
  return (
    <Sheet
      align="bottom"
      title={DELETE_ACCOUNT}
      testId="delete-account-sheet"
      onClose={onClose}
      zIndex={50}
    >
      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ font: '600 var(--fs-body)/1.4 var(--font-ui)', color: 'var(--red)' }}>
          {DELETE_WORLD_UNDO}
        </div>
        <div>
          <div style={{ font: '400 var(--fs-body)/1.4 var(--font-ui)' }}>{DELETE_ACCOUNT_GOES}</div>
          <ul
            style={{
              margin: '4px 0 0',
              paddingLeft: 20,
              font: '400 var(--fs-body)/1.5 var(--font-ui)',
            }}
          >
            {deleteAccountLoses().map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
        <label style={{ display: 'grid', gap: 4, font: '400 var(--fs-body)/1.4 var(--font-ui)' }}>
          {DELETE_ACCOUNT_TYPE}
          <input
            value={typed}
            data-testid="delete-account-word"
            aria-label={DELETE_ACCOUNT_INPUT}
            autoComplete="off"
            autoCapitalize="none"
            spellcheck={false}
            onInput={(e) => setTyped((e.currentTarget as HTMLInputElement).value)}
            style={{
              font: '400 var(--fs-name)/1.4 var(--font-ui)',
              padding: '0 12px',
              height: 44,
              boxSizing: 'border-box',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface)',
              color: 'var(--ink)',
            }}
          />
        </label>
        {error && (
          <div
            role="alert"
            style={{ font: '400 var(--fs-label)/1.4 var(--font-ui)', color: 'var(--red)' }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={onClose}>
            {DELETE_WORLD_CANCEL}
          </Button>
          <Button
            variant="danger"
            data-testid="delete-account-confirm"
            disabled={!ready || busy}
            {...(ready ? { explain: REASON_WAIT } : { reason: DELETE_ACCOUNT_NEEDS_WORD })}
            onClick={() => void confirm()}
          >
            {DELETE_ACCOUNT}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
